import React, { useCallback } from "react";
import styles from "./auto-sync-cases-settings-page.module.scss";
import { Button } from "@whatslly/ui-components/v2/Button";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import classnames from "classnames";
import { useDependencyInjector } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { CrmComponentsErrorBox } from "@whatslly/ui-components/v2/CrmComponentsErrorBox";
import { LOADER_IMG } from "@whatslly/commons/constants";
import type { IntervalOptionType } from "../../types/IntervalOptionType";
import { AutoSyncCasesSettingsStore } from "../../stores/AutoSyncCasesSettingsStore";
import { INTERVAL_OPTIONS } from "../../stores/IntervalOptions";

export const AutoSyncCasesSettingsPage: React.FC<{ hideSaveButton?: boolean }> = observer(({ hideSaveButton }) => {
  const autoSyncCasesSettingsStore = useDependencyInjector(AutoSyncCasesSettingsStore);

  const { t } = useTranslation();

  const runNow = useCallback(async () => {
    modalService.openModal(Toast, {
      text: t("autoSyncActivitiesPage.modal_start_now"),
    });
    await autoSyncCasesSettingsStore.runNow();
    modalService.openModal(Toast, {
      text: t("autoSyncActivitiesPage.modal_finish_now"),
    });
  }, [autoSyncCasesSettingsStore, t]);

  const saveSettings = useCallback(async () => {
    modalService.openModal(Toast, {
      text: t("autoSyncActivitiesPage.modal_saved_interval"),
    });
    await autoSyncCasesSettingsStore.saveSettings();
  }, [autoSyncCasesSettingsStore, t]);

  return (
    <div className={styles.page}>
      <div className={styles.header} data-testid={"auto-sync-cases"}>
        {t("autoSyncCasesPage.header")}
      </div>
      {autoSyncCasesSettingsStore.init.case({
        pending: () => (
          <div className={styles.loading}>
            <img alt="tuvis loader" src={LOADER_IMG} />
            <div>{t("autoSyncActivitiesPage.loading")}</div>
          </div>
        ),
        fulfilled: ({ showRunNowJob }) => (
          <>
            <div className={styles.desc}>{t("autoSyncCasesPage.desc")}</div>
            <div className={styles.select}>
              <Select<IntervalOptionType>
                disabled={autoSyncCasesSettingsStore.isSaving}
                options={INTERVAL_OPTIONS}
                value={autoSyncCasesSettingsStore.selectedOption}
                onChange={(item: IntervalOptionType) => autoSyncCasesSettingsStore.setSelectedOption(item)}
              />
            </div>
            <div className={styles.runNowSection}>
              <div>{autoSyncCasesSettingsStore.selectedOption?.description}</div>
              {showRunNowJob && (
                <div onClick={runNow} className={classnames(styles.runNowButton, { [styles.disabled]: autoSyncCasesSettingsStore.isRunning })}>
                  {t("autoSyncActivitiesPage.runNow")}
                </div>
              )}
            </div>
            {!hideSaveButton && (
              <Button
                isSubmitting={autoSyncCasesSettingsStore.isSaving}
                disabled={autoSyncCasesSettingsStore.isSaving}
                testId={`save-settings`}
                paddingInline="small"
                fontSize={`small`}
                onClick={saveSettings}
              >
                {t("autoSyncActivitiesPage.saveSettings")}
              </Button>
            )}
            {(autoSyncCasesSettingsStore.previousRun || autoSyncCasesSettingsStore.nextRun) && (
              <div className={styles.scheduleSection}>
                {autoSyncCasesSettingsStore.previousRun && (
                  <div data-testid="previous-run">
                    {t("autoSyncActivitiesPage.previousRun")}: {autoSyncCasesSettingsStore.previousRun}
                  </div>
                )}
                {autoSyncCasesSettingsStore.nextRun && (
                  <div data-testid="next-run">
                    {t("autoSyncActivitiesPage.nextRun")}: {autoSyncCasesSettingsStore.nextRun}
                  </div>
                )}
              </div>
            )}
          </>
        ),
        rejected: (error: Error) => <CrmComponentsErrorBox error={error.message} />,
      })}
    </div>
  );
});
